import { decode } from "utils/HelperFunctions";

export type PostLoginRoute = {
  pathname: string;
  search?: string;
  state?: Record<string, unknown>;
  clusterId: string;
};

export type SafePostLoginDestination = {
  to: string;
  state?: Record<string, unknown>;
};

const CLUSTER_ID_PATTERN = /^[1-9]\d{0,19}$/;
const INTERNAL_PATH_PATTERN = /^\/(?!\/)[^\\\u0000-\u001f]*$/;
const INTERNAL_SEARCH_PATTERN = /^(?:|\?[^#\u0000-\u001f]*)$/;

const getClusterIdFromPortalPath = (pathname: string): string | null => {
  const firstSegment = pathname.split("/").filter(Boolean)[0];

  if (!firstSegment?.startsWith("ci_")) {
    return null;
  }

  try {
    const clusterId = decode(firstSegment, true);
    return CLUSTER_ID_PATTERN.test(clusterId) ? clusterId : null;
  } catch {
    return null;
  }
};

/**
 * Validates a destination before it crosses the authentication boundary.
 * Only application-relative routes for the same cluster are accepted.
 */
export const getSafePostLoginDestination = (
  route: PostLoginRoute | null,
  authenticatedClusterId: string | null
): SafePostLoginDestination | null => {
  if (
    !route ||
    !authenticatedClusterId ||
    route.clusterId !== authenticatedClusterId ||
    !CLUSTER_ID_PATTERN.test(route.clusterId) ||
    !INTERNAL_PATH_PATTERN.test(route.pathname) ||
    !INTERNAL_SEARCH_PATTERN.test(route.search || "")
  ) {
    return null;
  }

  const pathClusterId = getClusterIdFromPortalPath(route.pathname);
  if (pathClusterId && pathClusterId !== authenticatedClusterId) {
    return null;
  }

  return {
    to: `${route.pathname}${route.search || ""}`,
    state: route.state,
  };
};
